import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import Cookies from "js-cookie";

import TenantAPI from "../../api/api/TenantAPI";
import { captureException } from "@sentry/react";

const publicRoutes = [
  "/login",
  "/login/forgot",
  "/login/reset",
  "/register",
  "/register/existing",
  "/register/phone",
  "/register/phone/verify",
  "/privacy",
  "/terms",
];

const startsWithRoutes = ["/shared-", "/share", "/forms"];

// 1 - If no auth token and tenant ID, redirect to login

// 2 - If no auth token, but route is valid non auth route, finish.

// 3 - Refresh Auth Token.

// 4 - If auth token and no cloud id in URL, redirect to clouds switcher page.
// 5 - If auth token and cloud id in URL and cloud auth token, redirect to cloud dashboard.
export default function useInitializeSession(
  getRefreshTokenRef: React.MutableRefObject<
    (useRedirect?: boolean, extraTenantID?: string) => Promise<any> | null
  >
) {
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const initializeSession = useMutation({
    mutationFn: async () => {
      try {
        setInitializing(true);

        if (pathname === "/oauth") return;

        const regex = /\/clouds\/([a-zA-Z0-9]+)/;
        const matches = location.pathname.match(regex);
        let tenantID;

        if (matches && matches.length > 1) {
          const [, cloudId] = matches;
          tenantID = cloudId;
        }

        let authToken = Cookies.get("authenticationToken");

        // 2 - Valid Routes, that don't require auth.
        if (
          !authToken &&
          (publicRoutes.includes(pathname) || checkStartsWithRoutes(pathname))
        ) {
          return setInitializing(false);
        }

        // 3 - Refresh Auth Token.
        await getRefreshTokenRef.current(false, tenantID);

        // 4 - If auth token and no cloud id in URL, redirect to clouds switcher page.
        if ((!tenantID && pathname.startsWith("/login")) || pathname === "/") {
          navigate("/clouds");
        }
      } catch (e) {
        await captureException({
          message: "Error initializing session in useInitializeSession",
          err: e,
        });
      } finally {
        setInitializing(false);
      }
    },
  });

  return {
    initializeSession: initializeSession.mutate,
    initializing: initializing,
  };
}

function checkStartsWithRoutes(pathname: string) {
  return startsWithRoutes.some((route) => pathname.startsWith(route));
}

export async function GetCloudToken(
  tenantID: string,
  setQueriesEnabled: React.Dispatch<React.SetStateAction<boolean>>,
  setCloudLogo: React.Dispatch<React.SetStateAction<string>>,
  onSuccess?: () => void
) {
  return TenantAPI.getAuthenticationToken(tenantID).then((data) => {
    setQueriesEnabled(true);
    setCloudLogo(data.data.data?.logo?.PublicURL ?? "");
    onSuccess && onSuccess();

    Cookies.set(
      `authenticationToken_${tenantID}`,
      data.data.data.authenticationToken,
      {
        expires: 7,
        sameSite: "strict",
        secure: window.location.origin.startsWith("https"),
      }
    );
  });
}
